"use client";

import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {Button, Fade, Stack} from "@mui/material";
import TourHeaderButtons from "@/components/product-detail-page/tour-header/tour-header-buttons/tour-header-buttons";

interface PdpStickyNavOptions {
  showPdpButtons: boolean;
  handleOpenRaq: () => void;
}

interface ScrollableStickyNavProps {
  navItems: { label: string; anchor: string }[];
  stickyTop?: string | number;
  pdpOptions?: PdpStickyNavOptions;
  moduleRankingPosition?: string;
}

const ScrollableStickyNav: React.FC<ScrollableStickyNavProps> = ({
  navItems,
  stickyTop,
  pdpOptions = undefined,
  moduleRankingPosition = "2"
}) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [isTouchDevice, setIsTouchDevice] = useState(true); // Default to touch for SSR
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isUserClicking = useRef(false);

  // Read banner height from CSS variable (managed by useBannerHeight hook in PromotionalBannerClient)
  useEffect(() => {
    const updateBannerHeight = () => {
      const rawValue = getComputedStyle(document.documentElement)
        .getPropertyValue("--banner-height")
        .trim();

      if (!rawValue) {
        setBannerHeight(0);
        return;
      }

      const parsed = Number.parseFloat(rawValue);
      const height = Number.isFinite(parsed) ? parsed : 0;
      setBannerHeight(height);
    };

    // Initial read
    updateBannerHeight();

    // Listen for changes (banner height can change on resize or breakpoint changes)
    // Use MutationObserver to watch for style changes on documentElement
    const observer = new MutationObserver(() => {
      updateBannerHeight();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"]
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Calculate the sticky top position based on actual banner height
  const baseTop = { legacyMobile: 80, legacyTablet: 78, legacyDesktop: 80 };
  const defaultStickyTop = bannerHeight > 0
    ? {
      legacyMobile: baseTop.legacyMobile + bannerHeight,
      legacyTablet: baseTop.legacyTablet + bannerHeight,
      legacyDesktop: baseTop.legacyDesktop + bannerHeight
    }
    : baseTop;

  const effectiveStickyTop = stickyTop ?? defaultStickyTop;

  // Detect touch device capability on mount
  useEffect(() => {
    const checkTouchDevice = () => {
      if (typeof window !== "undefined" && window.matchMedia) {
        setIsTouchDevice(window.matchMedia("(hover: none)").matches);
      }
    };

    checkTouchDevice();

    // Listen for changes (e.g., when switching between desktop/mobile in dev tools)
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(hover: none)");
      const handleChange = () => checkTouchDevice();

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      }
      // Fallback for older browsers  
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
    return undefined;
  }, []);

  const ensureActiveVisible = (idx: number, smooth = false) => {
    const container = scrollContainerRef.current;
    const el = itemRefs.current[idx];
    if (!container || !el) return;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;
    if (elLeft < viewLeft || elRight > viewRight) {
      const target = elLeft - (container.clientWidth - el.offsetWidth) / 2;
      container.scrollTo({
        left: Math.max(0, target),
        behavior: smooth ? "smooth" : "auto"
      });
    }
  };

  // Scroll to anchor on nav click
  const handleNavClick = (idx: number, anchor: string) => {
    isUserClicking.current = true;
    setActiveIdx(idx);
    ensureActiveVisible(idx, true); // Use smooth scrolling for user clicks
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Reset flag after scroll animation completes
    setTimeout(() => {
      isUserClicking.current = false;
    }, 1000);
  };

  // Highlight nav as user scrolls
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking && !isUserClicking.current) {
        requestAnimationFrame(() => {
          const navHeight = 234; // 10rem = 100px (adjust to match your scroll-margin-top)
          const offsets = navItems.map(item => {
            const el = document.getElementById(item.anchor);
            return el ? el.getBoundingClientRect().top : Infinity;
          });
          const lastVisible = offsets
            .map((top, idx) => ({ top, idx }))
            .filter(({ top }) => top <= navHeight)
            .pop()?.idx ?? 0;
          setActiveIdx(lastVisible);

          // find activesection bottom to deactivate all if scrolled past
          const activeSection = document.getElementById(navItems[lastVisible]?.anchor);
          const reachedActiveSectionBottom = activeSection?.getBoundingClientRect().bottom;
          if (reachedActiveSectionBottom && reachedActiveSectionBottom < 165) {
            setActiveIdx(500); // Set to an out-of-bounds index to deactivate all
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [navItems]);

  // When active index changes (due to scroll spy), keep the active item visible
  useEffect(() => {
    // Only auto-scroll the nav bar if user isn't actively clicking
    if (!isUserClicking.current) {
      ensureActiveVisible(activeIdx, false); // Use instant scrolling for scroll spy
    }
  }, [activeIdx]);

  return (
    <Box
        className="sticky-nav-border"
        sx={{
          position: "sticky",
          top:
            typeof effectiveStickyTop === "object"
              ? {
                legacyMobile: effectiveStickyTop.legacyMobile,
                legacyTablet: effectiveStickyTop.legacyTablet,
                legacyDesktop: effectiveStickyTop.legacyDesktop
              }
              : effectiveStickyTop,
          zIndex: 100,
          backgroundColor: "background.default"
        }}
        data-testid="scrollable-sticky-nav"
        component="section"
        data-track="visibility"
        data-module-name="In Page Navigation"
        data-parent-container-type="pdp"
        data-ranking-algorithm="manual"
        data-module-ranking-position={moduleRankingPosition}
      >
        <Box
          className="fluid-box"
          sx={{
            mx: 0,
            backgroundColor: "background.default",
            boxShadow: 6
          }}
        >
          <Box paddingY="1.6rem" className="destination-page-container" data-testid="destination-anchor-bar">
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                overflowY: "visible",
                p: { legacyDesktop: "0 7rem", legacyTablet: "0 7rem", legacyMobile: "0 2.4rem" },
                "&::-webkit-scrollbar": {
                  display: "none"
                },
                alignItems: "center",
                justifyContent: "space-between",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                gap: "1.2rem"
              }}
              ref={scrollContainerRef}
            >
              <Stack
                direction="row"
                gap={3}
              >
                {navItems.map((item, idx) => (
                  <Button
                    key={item.anchor}
                    onClick={() => handleNavClick(idx, item.anchor)}
                    ref={(el: HTMLButtonElement | null) => {
                      itemRefs.current[idx] = el;
                    }}
                    disableRipple={isTouchDevice}
                    disableFocusRipple
                    sx={{
                      padding: 0,
                      color: activeIdx === idx ? "primary.main" : "grey.700",
                      borderBottom: activeIdx === idx ? "2px solid" : "2px solid transparent",
                      borderRadius: 0,
                      textTransform: "none!important",
                      fontWeight: "700!important",
                      fontSize: "1.6rem!important",
                      minWidth: "max-content",
                      // Disable all interactive states on touch devices
                      "@media (hover: none)": {
                        "&:hover, &:focus, &:active, &:focus-visible": {
                          color: activeIdx === idx ? "primary.main" : "grey.700",
                          backgroundColor: "transparent"
                        }
                      },
                      // Only enable hover effects on devices with hover capability
                      "@media (hover: hover)": {
                        "&:hover, &:focus-visible": {
                          color: "secondary.main",
                          backgroundColor: "transparent"
                        }
                      }
                    }}
                    data-testid={`scrollable-nav-item-${idx}`}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
              {pdpOptions && (
                <Fade in={pdpOptions.showPdpButtons}>
                  <Box
                    sx={{
                      display: {
                        xs: "none",
                        lg: "block"
                      },
                      overflowY: "visible"
                    }}
                  >
                    <TourHeaderButtons handleOpenRequestAQuote={pdpOptions.handleOpenRaq} testId="sticky-nav" />
                  </Box>
                </Fade>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

export default ScrollableStickyNav;
